import { contentTopicRequest } from '../../questionBank/qbankNavigation';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import HomeDashboardLayout from "../HomeDashboardLayout";
import "../../dashboard/Dashboard.css";
import "../HomeDashboard.css";
import "../DashboardFavoriteExam.css";
import "../../DashboardQuestionBank.css";
import HeaderProfileSection from "../HeaderProfileSection";
import { useSidebarData } from "../../hooks/useSidebarData";
import { useFlashCardActions, useFlashCardDeck } from "../../hooks/useFlashCards";
import { buildQuestionBankFlowState, getStoredQuestionBankFlowState } from "../../questionBank/qbankFlowState";
import { resolveQuestionBankStateId } from "../../questionBank/stateUtils";
import { speakWithIndianVoices } from "../../utils/indianSpeech";
import { FlashCardListShimmer } from "./FlashCardShimmer";
import { FLASH_CARD_ROUTES } from "./flashCardRoutes";

import "./StartFlashCardd.css";
import "./CardList.css";

/* =========================================================
   CARD DATA
========================================================= */

const stillLearningCards = [
  {
    id: 1,

    title:
      "Who were the two Indian competitors who won bronze medals at EMO 2024?",

    answer: "Larissa (hisar) and Sai Patil (Pune) won bronze medals.",

    accent: "mint",
  },

  {
    id: 2,

    title:
      "What was the name of the tri-services conference held to discuss strategic reforms by the Indian Armed Forces?",

    answer: "The name of the conference was 'Parivartan Chintan'.",

    accent: "rose",
  },

  {
    id: 3,

    title: "Geographically, how was Katchathevu Island formed?",

    answer: "It was formed in the 14th century due to a volcanic eruption.",

    accent: "sky",
  },

  {
    id: 4,

    title:
      "Which state recorded the lowest voter turnout in the second phase of Lok Sabha elections 2024?",

    answer: "Uttar Pradesh (52.74%).",

    accent: "lavender",
  },

  {
    id: 5,

    title:
      "The RBI board decided to increase the Contingency Risk Buffer (CRB) by ________ for FY 2023-24.",

    answer: "6.50%",

    accent: "peach",
  },

  {
    id: 6,

    title:
      "What was the Contingency Risk Buffer (CRB) maintained by RBI in 2022-23?",

    answer: "6%",

    accent: "ice",
  },
];

/* =========================================================
   GET RESPONSIVE COLUMN COUNT
========================================================= */

const getColumnCount = () => {
  const width = window.innerWidth;

  /*
   * <= 400px
   * 1 card
   */

  if (width <= 400) {
    return 1;
  }

  /*
   * 401px - 767px
   * 2 cards
   */

  if (width <= 600) {
    return 2;
  }

  if (width <= 991) {
    return 3;
  }

  if (width <= 1199) {
    return 4;
  }
  /*
   * 768px - 1400px
   * 3 cards
   */

  if (width <= 1400) {
    return 3;
  }

  /*
   * > 1400px
   * 4 cards
   */

  return 4;
};

/* =========================================================
   MASONRY CARD COMPONENT
========================================================= */

const StillLearningMasonry = ({ cards }) => {
  const [columnCount, setColumnCount] = useState(getColumnCount());
  const [bookmarkOverrides, setBookmarkOverrides] = useState({});
  const { bookmark } = useFlashCardActions();

  const toggleBookmark = async (card) => {
    const currentStatus = bookmarkOverrides[card.id] ?? card.bookmarkStatus;
    const nextStatus = currentStatus ? 0 : 1;

    setBookmarkOverrides((current) => ({ ...current, [card.id]: nextStatus }));
    try {
      await bookmark.mutateAsync({ flash_card_id: card.id });
    } catch {
      setBookmarkOverrides((current) => ({ ...current, [card.id]: currentStatus }));
    }
  };

  const speakCard = (card) => {
    if (!window.speechSynthesis || !window.SpeechSynthesisUtterance) return;

    window.speechSynthesis.cancel();
    const text = [card.title, card.answer].filter(Boolean).join(". ");
    speakWithIndianVoices(text);
  };

  /*
   * Store each card DOM element
   */

  const cardRefs = useRef({});

  /*
   * Store calculated positions
   */

  const [positions, setPositions] = useState([]);

  /*
   * Force recalculation counter
   */

  const [layoutVersion, setLayoutVersion] = useState(0);

  /* =========================================================
     RESPONSIVE COLUMN COUNT
  ========================================================= */

  useEffect(() => {
    const handleResize = () => {
      const newColumnCount = getColumnCount();

      setColumnCount((previous) => {
        if (previous === newColumnCount) {
          return previous;
        }

        return newColumnCount;
      });
    };

    handleResize();

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  /* =========================================================
     RECALCULATE LAYOUT
  ========================================================= */

  const calculateLayout = () => {
    if (!cards.length) {
      return;
    }

    const columnHeights = new Array(columnCount).fill(0);

    const newPositions = [];

    /*
     * Cards remain in original order.
     *
     * Example for 4 columns:
     *
     * CARD 1 -> column 0
     * CARD 2 -> column 1
     * CARD 3 -> column 2
     * CARD 4 -> column 3
     * CARD 5 -> column 0
     * CARD 6 -> column 1
     */

    cards.forEach((card, index) => {
      const element = cardRefs.current[card.id];

      if (!element) {
        return;
      }

      const cardHeight = element.getBoundingClientRect().height;

      /*
       * FIXED COLUMN
       *
       * Never use shortest column.
       */

      const columnIndex = index % columnCount;

      /*
       * Current top position
       * inside this column.
       */

      const top = columnHeights[columnIndex];

      newPositions.push({
        id: card.id,

        column: columnIndex,

        top: top,

        height: cardHeight,
      });

      /*
       * Only this column is increased.
       */

      columnHeights[columnIndex] = top + cardHeight + 12;
    });

    setPositions(newPositions);
  };

  /* =========================================================
     INITIAL LAYOUT
  ========================================================= */

  useLayoutEffect(() => {
    /*
     * Wait for DOM paint.
     */

    const timer = setTimeout(() => {
      calculateLayout();
    }, 50);

    return () => {
      clearTimeout(timer);
    };
  }, [cards, columnCount, layoutVersion]);

  /* =========================================================
     OBSERVE CARD HEIGHT
  ========================================================= */

  useEffect(() => {
    if (!cards.length) {
      return;
    }

    const observer = new ResizeObserver(() => {
      /*
       * Don't directly call setPositions here
       * multiple times.
       *
       * Just trigger layout.
       */

      setLayoutVersion((version) => version + 1);
    });

    cards.forEach((card) => {
      const element = cardRefs.current[card.id];

      if (element) {
        observer.observe(element);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, [cards, columnCount]);

  /* =========================================================
     CONTAINER HEIGHT
  ========================================================= */

  const containerHeight =
    positions.length > 0
      ? Math.max(...positions.map((item) => item.top + item.height))
      : 0;

  /* =========================================================
     CALCULATE CARD WIDTH
  ========================================================= */

  const cardWidth = `calc(
      (100% - ${(columnCount - 1) * 12}px) / ${columnCount}
    )`;

  /* =========================================================
     CALCULATE CARD LEFT
  ========================================================= */

  const getCardLeft = (column) => {
    return `calc(
      ${column} *
      (
        (100% - ${(columnCount - 1) * 12}px) /
        ${columnCount}
      )
      +
      ${column * 12}px
    )`;
  };

  /* =========================================================
     RENDER CARD
  ========================================================= */

  const renderCard = (card) => {
    const position = positions.find((item) => item.id === card.id);

    return (
      <article
        key={card.id}
        ref={(element) => {
          cardRefs.current[card.id] = element;
        }}
        className={`
          still-learning-card
          still-learning-card--${card.accent}
        `}
        style={
          position
            ? {
                position: "absolute",

                top: `${position.top}px`,

                left: getCardLeft(position.column),

                width: cardWidth,
              }
            : {
                position: "absolute",

                visibility: "hidden",

                width: cardWidth,
              }
        }
      >
        {/* =================================================
            CARD HEADER
        ================================================= */}

        <div className="still-learning-card__header d-flex align-items-center justify-content-between">
          <span className="still-learning-card__head blackText">
            CARD {card.cardIndex}
          </span>

          <div className="still-learning-card__actions d-flex align-items-center">
            {/* VOLUME */}

            <button type="button" aria-label="Listen to card" onClick={() => speakCard(card)}>
              <i className="bi bi-volume-up blackText"></i>
            </button>

            {/* STAR */}

            <button
              type="button"
              aria-label={bookmarkOverrides[card.id] ?? card.bookmarkStatus ? "Remove bookmark" : "Bookmark card"}
              aria-pressed={Boolean(bookmarkOverrides[card.id] ?? card.bookmarkStatus)}
              onClick={() => toggleBookmark(card)}
              disabled={bookmark.isPending}
            >
              <i className={`bi ${bookmarkOverrides[card.id] ?? card.bookmarkStatus ? "bi-star-fill" : "bi-star"} blackText`}></i>
            </button>
          </div>
        </div>

        {/* =================================================
            CARD BODY
        ================================================= */}

        <div className="still-learning-card__body">
          <div className="still-learning-card__panel">
            {/* QUESTION */}

            <h3 className="blackText">{card.title}</h3>

            {/* ANSWER */}

            <p className="grayText">{card.answer}</p>
          </div>
        </div>
      </article>
    );
  };

  /* =========================================================
     RETURN
  ========================================================= */

  return (
    <div
      className="still-learning-masonry"
      style={{
        height: `${containerHeight}px`,
      }}
    >
      {cards.map((card) => renderCard(card))}
    </div>
  );
};

/* =========================================================
   MAIN PAGE
========================================================= */

const CardList = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { data: sidebarData = null } = useSidebarData();
  const flowState = useMemo(() => buildQuestionBankFlowState(location.state, getStoredQuestionBankFlowState()), [location.state]);
  const stateId = resolveQuestionBankStateId({ locationState: flowState, sidebarData }) || 13;
  const subjectId = Number(flowState?.selectedSubject?.id || flowState?.subject_id || 0);
  const cardProgress = flowState?.card_progress || "know_card";
  const params = useMemo(() => ({ states_id: stateId, category_id: 9, subject_id: subjectId, card_progress: cardProgress, ...contentTopicRequest(flowState) }), [cardProgress, flowState, stateId, subjectId]);
  const deckQuery = useFlashCardDeck(params, Boolean(subjectId));
  const cards = (deckQuery.data?.pages.flatMap((page) => page?.payload?.flash_cards_list || []) || []).map((card, index) => ({ id: Number(card.flash_card_id), cardIndex: card.flash_card_index || index + 1, bookmarkStatus: Number(card.bookmark_status) === 1, title: card.name || "", answer: card.description || "", accent: ["mint", "rose", "sky", "lavender", "peach", "ice"][index % 6] }));
  const totalCards = deckQuery.data?.pages?.[0]?.payload?.pagination?.totalRecords || cards.length;
  const openStudy = () => navigate(FLASH_CARD_ROUTES.study, { state: buildQuestionBankFlowState({ ...flowState, card_progress: cardProgress, qbankBackTarget: { pathname: FLASH_CARD_ROUTES.list, state: flowState } }) });
  return (
    <>
      {/* =====================================================
          PAGE OVERRIDE
      ===================================================== */}

      <style>{`

        .homeDashboard-wrapper
        .page-content {
          padding-right: 0;
        }
        .bottom-fixed-button {
         background: transparent;
        }

      `}</style>

      <HomeDashboardLayout showHeader={false}>
        <div className="qbl-page qbankList-page podcastListt">
          <div className="qbl-main" aria-label="Question bank content">
            <section className="qbl-panel">
              {/* =================================================
                  HEADER
              ================================================= */}

              <div className="right-notify d-flex justify-content-between align-items-center">
                {/* BACK BUTTON */}

                <div>
                  <button type="button" className="categories-back-button" onClick={() => flowState?.qbankBackTarget?.pathname ? navigate(flowState.qbankBackTarget.pathname, { state: flowState.qbankBackTarget.state }) : navigate(FLASH_CARD_ROUTES.start, { state: flowState })}>
                    <i className="bi bi-chevron-left"></i>
                  </button>
                </div>

                {/* PAGE TITLE */}

                <div>
                  <h4 className="d-flex categories-page-title">
                    {cardProgress.replaceAll("_", " ")} ({totalCards})
                  </h4>
                </div>

                {/* PROFILE */}

                <div>
                  <HeaderProfileSection />
                </div>
              </div>

              {/* =================================================
                  BODY
              ================================================= */}

              <div className="qb-body">
                <div className="d-flex align-items-center justify-content-center">
                  <div className="qb-copy">
                    {/* =================================================
                        CARDS
                    ================================================= */}

                    <div className="still-learning-shell">
                      {deckQuery.isLoading ? <FlashCardListShimmer /> : null}
                      {deckQuery.isError ? <div className="choose-state__status choose-state__status--error">Unable to load Flash Cards.</div> : null}
                      {!deckQuery.isLoading && !deckQuery.isError && !cards.length ? <div className="choose-state__status">No cards are available in this list.</div> : null}
                      {cards.length ? <StillLearningMasonry cards={cards} /> : null}
                    </div>

                    {/* =================================================
                        FIXED PLAY BUTTON
                    ================================================= */}

                    <div className="bottom-fixed-button">
                      <button
                        type="button"
                        className="selected-subjects__checkout"
                        onClick={openStudy}
                        disabled={!cards.length}
                      >
                        Play Card
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </HomeDashboardLayout>
    </>
  );
};

export default CardList;
